import React, { useEffect, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney } from '../shared/formatting';
import { NiceLineChart } from '../shared/charts';
import { Page, PageHeader, Card, StatCard, DataTable, type Col } from '../shared/ui';

/**
 * Modul 1b "trosak robe" (m.1.2) — margin proxy iz robnog knjigovodstva.
 *
 * Dva odvojena kanala, prikazana kao dvije sekcije na istoj stranici:
 *  - ADO: prodaja (bi_m1b_offers_unified) vs nabava kod Argor/StoneX/C-Hafner
 *    (bi_argor_fixing_lines, filtrirano na zlato).
 *  - Lom (ADMS): otkup od gradjana (bi_m1b_buyback_unified) vs izlazne
 *    kalkulacije prema TCA (bi_lom_tca_calculations) s GK booking statusom.
 *
 * Backend: App\Support\GoldCogs\Pipeline. Oba izracuna su NAMJERNO oznacena
 * kao proxy (caveat tekst iz API odgovora se prikazuje ispod naslova svake
 * sekcije) — vidi docblock migracije bi_lom_tca_calculations za puni kontekst
 * (COGS jos nije proknjizen u GK ni za ADO ni za ADMS lom, konto 224000
 * potrazuje 0,00 kroz cijelu 2026).
 */

type SummaryPayload = {
    range: { from: string; to: string };
    ado: { revenue_eur: number; cost_eur: number; margin_eur: number; margin_pct: number | null; caveat: string };
    lom: { otkup_eur: number; tca_output_eur: number; tca_knjizeno_eur: number; tca_knjizeno_pct: number | null; flow_diff_eur: number; caveat: string };
};

type TrendRow = { period: string; revenue_eur: number; cost_eur: number; margin_eur: number; margin_pct: number | null };
type OtkupRow = { period: string; otkup_eur: number; tca_output_eur: number; tca_knjizeno_eur: number };
type StatusRow = { booking_status: string; broj_dokumenata: number; iznos_eur: number; pct_of_total: number };

const STATUS_LABEL: Record<string, string> = {
    knjizeno: 'Knjiženo',
    storno: 'Storno',
    neproknjizeno: 'Neproknjiženo',
};

export function GoldCogsModule({ token, locale = 'hr' }: { token: string; locale?: 'hr' | 'en' }) {
    const [summary, setSummary] = useState<SummaryPayload | null>(null);
    const [marginTrend, setMarginTrend] = useState<TrendRow[] | null>(null);
    const [otkupTrend, setOtkupTrend] = useState<OtkupRow[] | null>(null);
    const [statusRows, setStatusRows] = useState<StatusRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [s, mt, ot, st] = await Promise.all([
                    api<SummaryPayload>('/gold-cogs/summary', token),
                    api<{ rows: TrendRow[] }>('/gold-cogs/margin-trend?granularity=mjesec', token),
                    api<{ rows: OtkupRow[] }>('/gold-cogs/otkup-trend?granularity=mjesec', token),
                    api<{ rows: StatusRow[] }>('/gold-cogs/lom-tca-status', token),
                ]);
                if (cancelled) return;
                setSummary(s);
                setMarginTrend(mt.rows);
                setOtkupTrend(ot.rows);
                setStatusRows(st.rows);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Greška pri dohvatu podataka.');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const statusCols: Col<StatusRow & { id: string }>[] = [
        { key: 'booking_status', label: 'Status', render: (r) => STATUS_LABEL[r.booking_status] ?? r.booking_status },
        { key: 'broj_dokumenata', label: 'Dokumenata', align: 'right' },
        { key: 'iznos_eur', label: 'Iznos', align: 'right', render: (r) => formatMoney(r.iznos_eur, locale) },
        { key: 'pct_of_total', label: '% ukupno', align: 'right', render: (r) => `${r.pct_of_total.toFixed(1)}%` },
    ];

    return (
        <Page>
            <PageHeader
                icon="⚖️"
                title="Trošak robe (zlato)"
                subtitle="Margin proxy iz robnog knjigovodstva — ADO prodaja/nabava i lom otkup/TCA izlaz, s GK booking statusom."
            />

            {error ? (
                <Card pad={16} style={{ marginBottom: 16, color: '#b14040' }}>{error}</Card>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 10 }}>
                <StatCard icon="💰" color="#34383d" label="ADO prihod (ponude)" value={summary ? formatMoney(summary.ado.revenue_eur, locale) : '…'} />
                <StatCard icon="🏭" color="#5c6b7f" label="ADO nabava (Argor/StoneX/C-Hafner)" value={summary ? formatMoney(summary.ado.cost_eur, locale) : '…'} />
                <StatCard
                    icon="📊"
                    color={summary && summary.ado.margin_eur < 0 ? '#b14040' : '#1f7a36'}
                    label="ADO margin (proxy)"
                    value={summary ? formatMoney(summary.ado.margin_eur, locale) : '…'}
                    sub={summary?.ado.margin_pct != null ? `${summary.ado.margin_pct.toFixed(1)}%` : undefined}
                />
            </div>
            {summary ? <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 24 }}>{summary.ado.caveat}</div> : null}

            <Card pad={16} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 10 }}>ADO margin trend (mjesečno)</div>
                {marginTrend ? (
                    <NiceLineChart
                        locale={locale}
                        xLabels={marginTrend.map((r) => r.period)}
                        series={[
                            { name: 'Prihod', color: '#5c6b7f', values: marginTrend.map((r) => r.revenue_eur) },
                            { name: 'Nabava', color: '#888', values: marginTrend.map((r) => r.cost_eur) },
                            { name: 'Margin', color: '#1f7a36', values: marginTrend.map((r) => r.margin_eur) },
                        ]}
                    />
                ) : <div style={{ padding: 20, color: '#999' }}>Učitavanje…</div>}
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 10 }}>
                <StatCard icon="🪙" color="#34383d" label="Lom otkup (nabavna)" value={summary ? formatMoney(summary.lom.otkup_eur, locale) : '…'} />
                <StatCard icon="🚚" color="#5c6b7f" label="Izlaz prema TCA (ukupno)" value={summary ? formatMoney(summary.lom.tca_output_eur, locale) : '…'} />
                <StatCard
                    icon="✅"
                    color={summary && (summary.lom.tca_knjizeno_pct ?? 0) < 10 ? '#b14040' : '#1f7a36'}
                    label="Od toga knjiženo u GK"
                    value={summary ? formatMoney(summary.lom.tca_knjizeno_eur, locale) : '…'}
                    sub={summary?.lom.tca_knjizeno_pct != null ? `${summary.lom.tca_knjizeno_pct.toFixed(1)}%` : undefined}
                    subColor={summary && (summary.lom.tca_knjizeno_pct ?? 0) < 10 ? '#b14040' : undefined}
                />
            </div>
            {summary ? <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 24 }}>{summary.lom.caveat}</div> : null}

            <Card pad={16} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 10 }}>Lom: otkup vs izlaz prema TCA (mjesečno)</div>
                {otkupTrend ? (
                    <NiceLineChart
                        locale={locale}
                        xLabels={otkupTrend.map((r) => r.period)}
                        series={[
                            { name: 'Otkup', color: '#5c6b7f', values: otkupTrend.map((r) => r.otkup_eur) },
                            { name: 'Izlaz TCA', color: '#888', values: otkupTrend.map((r) => r.tca_output_eur) },
                            { name: 'Od toga knjiženo', color: '#1f7a36', values: otkupTrend.map((r) => r.tca_knjizeno_eur) },
                        ]}
                    />
                ) : <div style={{ padding: 20, color: '#999' }}>Učitavanje…</div>}
            </Card>

            <Card pad={16}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 10 }}>Kalkulacije lom→TCA po GK statusu</div>
                <DataTable
                    cols={statusCols}
                    rows={statusRows ? statusRows.map((r) => ({ ...r, id: r.booking_status })) : null}
                    empty="Nema kalkulacija u odabranom razdoblju."
                />
            </Card>
        </Page>
    );
}
